import {
  InnerContentItem,
  InnerContentLink,
  InnerContentDivider,
  OuterContentItem,
  PrimaryTag,
  List,
  ListItem,
  SummaryMessage,
  Text,
} from "@certn/cascadia-react";
import { SubHeading } from "@certn/cascadia-react/components/summary-card/sub-heading/SubHeading";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { formatDate } from "@certn/utils";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  getReferencesRuleResults,
  getReferenceDate,
  useSkipReason,
} from "./utils";

export function EmployerReferenceSummaryField({
  field,
}: {
  field: components["schemas"]["EmploymentReferencesCheckField"];
}) {
  const { t } = useTranslation();
  const { applicantId } = useParams();

  const { isValid, requiredCount, requireAmount } = getReferencesRuleResults({
    field,
    values: field.value,
  });

  const currentCount = field.value?.references?.length;
  const skipReason = field.value?.employer_reference_skip_reason;
  const { getTranslatedReason } = useSkipReason();

  return (
    <>
      {isValid === false && (
        <SummaryMessage type="warning">
          <Text
            marginTop="space-05"
            style="global/heading-small"
            testID="employment-summary-employment-references-incomplete"
          >
            <Trans>Employment references incomplete</Trans>
          </Text>
          <List
            type="unordered"
            size="secondary"
            marginTop="space-1"
            testID="employment-summary-employment-references-needed"
          >
            {requireAmount && (
              <ListItem>
                <Trans>
                  References provided: {{ currentCount }} of {{ requiredCount }}{" "}
                  employer references
                </Trans>
              </ListItem>
            )}
            {skipReason && (
              <ListItem>
                <Trans>Reason</Trans>: {getTranslatedReason(skipReason)}
              </ListItem>
            )}
          </List>
        </SummaryMessage>
      )}
      {field.value.references
        .sort((a) => (a.end_date ? 1 : -1))
        .map((reference, index) => {
          return (
            <OuterContentItem
              key={reference.id}
              title={reference.first_name + " " + reference.last_name}
              subtitle={getReferenceDate(reference)}
              tag={
                !reference.end_date && (
                  <PrimaryTag type="subtle">
                    <Trans>Current</Trans>
                  </PrimaryTag>
                )
              }
              testID={`employment-summary-employment-${index + 1}`}
            >
              <SubHeading>
                <Trans>Employment details</Trans>
              </SubHeading>
              <InnerContentItem
                label={t("Company")}
                testID={`employment-summary-employment-${index + 1}-company`}
              >
                {reference.company_name}
              </InnerContentItem>
              <InnerContentItem
                label={t("Position")}
                testID={`employment-summary-employment-${index + 1}-position`}
              >
                {reference.position}
              </InnerContentItem>
              <InnerContentItem
                label={t("Start date")}
                testID={`employment-summary-employment-${index + 1}-start-date`}
              >
                {formatDate(reference.start_date, "MMMM yyyy")}
              </InnerContentItem>
              {reference.end_date && (
                <InnerContentItem
                  label={t("End date")}
                  testID={`employment-summary-employment-${index + 1}-end-date`}
                >
                  {formatDate(reference.end_date, "MMMM yyyy")}
                </InnerContentItem>
              )}

              <InnerContentDivider />

              <SubHeading>
                <Trans>Reference details</Trans>
              </SubHeading>
              <InnerContentItem
                label={t("Full name")}
                testID={`employment-summary-employment-${index + 1}-full-name`}
              >
                {reference.first_name + " " + reference.last_name}
              </InnerContentItem>
              <InnerContentItem
                label={t("Job title")}
                testID={`employment-summary-employment-${index + 1}-job-title`}
              >
                {reference.reference_job_title}
              </InnerContentItem>
              {field.require_email && (
                <InnerContentItem
                  label={t("Email")}
                  testID={`employment-summary-employment-${index + 1}-email`}
                >
                  {reference.email}
                </InnerContentItem>
              )}
              {field.require_phone && (
                <InnerContentItem
                  label={t("Phone number")}
                  testID={`employment-summary-employment-${
                    index + 1
                  }-phone-number`}
                >
                  {reference.phone_number}
                </InnerContentItem>
              )}
              <InnerContentItem
                label={t("Reference relationship")}
                testID={`employment-summary-employment-${
                  index + 1
                }-relationship`}
              >
                {reference.relationship}
              </InnerContentItem>
              <InnerContentLink
                url={`/apply/${applicantId}/employment-references?reference=${reference.id}`}
                testID={`employment-summary-employment-${index + 1}-edit-link`}
              >
                <Trans>Edit</Trans>
              </InnerContentLink>
            </OuterContentItem>
          );
        })}
    </>
  );
}
